import { logOperationalEvent, logWarn } from 'saddlebag-logger';

import {
  getCachedWillShowBanner,
  getConsentCookieName,
  getConsumer,
  isKetchMode,
} from './cmp-state';
import { COMPONENT, CONSENT_COOKIE_PARSE_FAILURE } from './constants/events';
import { registerKetchJurisdictionLookup } from './isomorphic';
import {
  getKetchCookieValue,
  hasActiveConsent,
  parseKetchConsentCookie,
} from './ketch-cookie-utils';
import {
  isFallbackActive,
  getFallbackConsent,
  UNKNOWN_JURISDICTION,
} from './ketch-fallback';

import type { KetchConsentCookie, KetchConsentData } from './ketch-types';

/**
 * Determines if consent banner should be shown.
 *
 * Decision logic:
 * - Cached value available (client-side, post-init) → return cached decision
 * - No cookie → true (show banner)
 * - Malformed cookie → true (show banner - safe fallback)
 * - Empty purposes → true (show banner)
 * - Purposes present → false (user has made a consent decision, don't show)
 *
 * @param cookieString - Optional cookie header string for server-side use.
 *                       If omitted, reads from document.cookie (browser).
 * @returns true if banner should be shown, false otherwise
 */
export function willShowConsentBanner(cookieString?: string | null): boolean {
  if (cookieString === undefined) {
    const cached = getCachedWillShowBanner();
    if (cached !== undefined) {
      return cached;
    }
  }

  const cookieValue = getKetchCookieValue(getConsentCookieName(), cookieString);
  if (!cookieValue) {
    return true; // No cookie → show banner
  }

  return !hasActiveConsent(cookieValue);
}

/**
 * Gets current Ketch consent state from the _swb_consent_ cookie.
 *
 * If fallback consent is active (SDK load failed, no previous consent),
 * returns the fallback reject-all state instead of null.
 *
 * @param cookieString - Optional cookie header string for server-side use.
 *                       If omitted, reads from document.cookie (browser).
 * @returns Parsed consent data with normalized boolean values, fallback consent if active, or null
 */
export function getConsent(
  cookieString?: string | null,
): KetchConsentData | null {
  const cookieValue = getKetchCookieValue(getConsentCookieName(), cookieString);
  if (!cookieValue) {
    // No cookie - check if fallback is active
    if (isFallbackActive()) {
      return getFallbackConsent();
    }
    return null;
  }

  try {
    const parsed = parseKetchConsentCookie(cookieValue);
    return normalizeConsentData(parsed);
  } catch (error) {
    logOperationalEvent({
      eventName: CONSENT_COOKIE_PARSE_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
    });
    logWarn({
      component: COMPONENT,
      message: `${CONSENT_COOKIE_PARSE_FAILURE}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
    return null;
  }
}

/**
 * Returns the known Ketch jurisdiction, or undefined if not in Ketch mode,
 * no consent cookie exists yet, or only session-only fallback consent is
 * active (jurisdiction not yet known in all these cases).
 *
 * Registered with isomorphic.ts's isRegulatedMarket via
 * registerKetchJurisdictionLookup to break the circular import between
 * isomorphic.ts and cmp-state.ts/ketch-consent.ts.
 *
 * @returns The consent cookie's jurisdiction, or undefined
 */
export function getKnownKetchJurisdiction(): string | undefined {
  if (!isKetchMode()) return undefined;
  const jurisdiction = getConsent()?.jurisdiction;
  if (!jurisdiction || jurisdiction === UNKNOWN_JURISDICTION) {
    return undefined;
  }
  return jurisdiction;
}

registerKetchJurisdictionLookup(getKnownKetchJurisdiction);

/**
 * Normalizes raw Ketch cookie data to KetchConsentData.
 * Converts string "true"/"false" to boolean.
 *
 * @param raw - Raw KetchConsentCookie from the cookie
 * @returns Normalized KetchConsentData with boolean allowed values
 */
function normalizeConsentData(raw: KetchConsentCookie): KetchConsentData {
  const purposes: Record<string, { allowed: boolean; legalBasisCode: string }> =
    {};

  for (const [code, data] of Object.entries(raw.purposes || {})) {
    purposes[code] = {
      allowed: data.allowed === 'true',
      legalBasisCode: data.legalBasisCode,
    };
  }

  return {
    purposes,
    jurisdiction: raw.jurisdictionCode,
    collectedAt: raw.collectedAt,
    propertyCode: raw.propertyCode,
  };
}
